import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard, { ProductModal } from "@/components/ProductCard";

const CATEGORY_LABELS = {
  "conflict-management": "Conflict Management",
  "emotional-intelligence": "Emotional Intelligence",
  "leadership": "Leadership",
  "self-leadership": "Self Leadership",
  "bundles": "Bundles",
};

const ProductGrid = ({ products, showFilter = true }) => {
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const categories = useMemo(() => {
    const seen = new Set();
    products.forEach((p) => seen.add(p.category));
    return ["all", ...Array.from(seen)];
  }, [products]);

  const filtered = useMemo(() =>
    category === "all" ? products : products.filter((p) => p.category === category)
  , [category, products]);

  // Deep-link support: `?product=<id>` auto-opens the matching product modal
  // and scrolls its card into view. Removes the param after opening so refreshes
  // don't re-trigger.
  useEffect(() => {
    const target = searchParams.get("product");
    if (!target || !products.length) return;
    const match = products.find((p) => p.id === target);
    if (!match) return;
    setSelected(match);
    setOpen(true);
    // Slight delay so the card is in the DOM before scrolling
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="product-card-${target}"]`);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 250);
    // Strip the query param
    const next = new URLSearchParams(searchParams);
    next.delete("product");
    setSearchParams(next, { replace: true });
  }, [products, searchParams, setSearchParams]);

  return (
    <div>
      {showFilter && (
        <div className="flex flex-wrap gap-2 mb-10" data-testid="product-filter">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 text-xs uppercase tracking-wider rounded-sm border transition-colors duration-200 ${
                category === c
                  ? "bg-[#8C4A57] border-[#8C4A57] text-white"
                  : "bg-white border-[#E8DCDD] text-[#2D2628] hover:border-[#8C4A57] hover:text-[#8C4A57]"
              }`}
            >
              {c === "all" ? "All" : CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="product-grid">
        {filtered.map((p) => (
          <ProductCard key={p.id} product={p} onOpen={(prod) => { setSelected(prod); setOpen(true); }} />
        ))}
      </div>
      <ProductModal product={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
};

export default ProductGrid;
